'use client'
import * as React from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { MainContribuitor } from './main-contribuitor'
import { SecondaryContribuitor } from './secondary-contribuitor'
import { useDashboardPresenceQuery } from '~/hooks-data/use-dashboard-presence-query'
import { Repository, Team } from '@basehub/data/replicache/index'
import { PublicTeam } from '@basehub/data/replicache/mutators/_public-types'
import { clsx } from 'clsx'
import { $avatar } from '~/components/basehub-avatar'
import { TooltipPortal } from '@radix-ui/react-tooltip'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'
import { isPlaygroundUser } from '@basehub/data/replicache/playground/ids'

const SHOWED_CONTRIBUTORS = 5

export const Contributors: React.FC<{
  team: Team | PublicTeam
  repository: Pick<Repository, 'id'>
  contributorsIds?: string[]
  className?: string
  avatarSize?: number
  collisionBoundary?: HTMLElement | HTMLElement[] | null
  showedContributors?: number
}> = ({
  contributorsIds = [],
  className,
  team,
  repository,
  avatarSize,
  collisionBoundary,
  showedContributors = SHOWED_CONTRIBUTORS,
}) => {
  const { data: dashboardPresence } = useDashboardPresenceQuery(team.id)
  const { branch } = useSelectedBranch()
  const repoPresence = dashboardPresence?.filter((c) => {
    // if dealing with non-logged in user from playground, only show them in the playground branch.
    if (isPlaygroundUser(c.userId)) {
      return branch?.id === c.branchId
    }

    return c.repoId === repository.id
  })

  const contributorsAndActivePeople = React.useMemo(() => {
    return [
      ...new Set([
        ...contributorsIds,
        ...(repoPresence?.map((c) => c.userId) ?? []),
      ]),
    ]
  }, [contributorsIds, repoPresence])

  const sortedByPresence = React.useMemo(() => {
    return [...contributorsAndActivePeople].sort((a, b) => {
      const aIsActive = repoPresence?.some((c) => c.userId === a)
      const bIsActive = repoPresence?.some((c) => c.userId === b)
      if (aIsActive === bIsActive) return 0
      if (aIsActive) return -1
      return 1
    })
  }, [contributorsAndActivePeople, repoPresence])

  const firstContributors = sortedByPresence?.slice(0, showedContributors) ?? []
  const otherContributorsUsernames =
    sortedByPresence?.slice(showedContributors) ?? []
  const hasMoreThan3Contributors = otherContributorsUsernames.length > 0

  let borderSize = 1
  if (avatarSize && avatarSize > 20) {
    borderSize = 2
  } else if (avatarSize && avatarSize > 10) {
    borderSize = 1.5
  }

  return (
    <div className={clsx(className, 'flex items-center h-6')}>
      <div className="flex items-center group/avatars">
        {firstContributors.map((userId, index) => {
          const activeClient = repoPresence?.find((c) => c.userId === userId)

          return (
            <MainContribuitor
              activeClient={activeClient ?? null}
              key={userId}
              userId={userId}
              collisionBoundary={collisionBoundary}
              borderSize={borderSize}
              className={clsx(
                'relative transition-all duration-200 ease-in-out',
                index !== 0 && 'group-hover/avatars:-ml-[3px] -ml-2',
                'relative'
              )}
              style={
                {
                  ...(avatarSize && { width: avatarSize, height: avatarSize }),
                } as React.CSSProperties
              }
            />
          )
        })}
        {hasMoreThan3Contributors && (
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <span
                className={$avatar({
                  className:
                    'text-dark-muted border-dark-border-solid -ml-2 p-0.5 !w-auto min-w-6 text-[11px] flex items-center justify-center relative group-hover/avatars:-ml-[3px] transition-all ease-in-out',
                })}
                style={
                  avatarSize
                    ? {
                        width: avatarSize,
                        height: avatarSize,
                        borderWidth: borderSize,
                      }
                    : undefined
                }
              >
                +{contributorsIds.length - firstContributors.length}
              </span>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                collisionBoundary={collisionBoundary}
                className="py-1 !px-2"
              >
                {otherContributorsUsernames.map((userId, index) => (
                  <SecondaryContribuitor key={index} userId={userId} />
                ))}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
